import Rollbar from 'rollbar';

import { toError } from '@repo/lib/errors';
import { redactSensitiveInfo } from '@repo/lib/strings';

import { env } from './env';
import { getRetailerFromUrl } from './getRetailerFromUrl';
import { getParams } from './searchParams';
import { sanitizePayloadCustom } from './rollbarSanitize';
import { isAbortError, isNetworkError } from './errorClassification';
import { redactUrl } from './redactUrl';
import { getNetworkOperation } from './networkErrorMeta';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Rollbar `payload.custom` merge state (configure does not always deep-merge). */
let rollbarCustomPayload: Record<string, string> = {};

let lastKnownRouteForErrors = '';

/**
 * Network failures fired while the page is unloading are the browser tearing
 * down in-flight requests, not real errors. On mobile they arrive in bursts at
 * navigation/lock and would otherwise dominate the project. A backgrounded but
 * still-alive tab can run real work (polling, retries), so only terminal unload
 * (`pagehide`) drops the failure — a merely-hidden tab still downgrades to
 * `warning` like any other network error.
 */
let pageIsUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageIsUnloading = true;
  });
  // bfcache restores fire `pageshow`, so reset here or a restored page keeps
  // dropping real failures.
  window.addEventListener('pageshow', () => {
    pageIsUnloading = false;
  });
}

const redactString = (value: unknown): unknown =>
  typeof value === 'string' ? redactSensitiveInfo(value) : value;

const canonicalAssetHost = (): string | null => {
  switch (env.VITE_ENV_NAME) {
    case 'production':
      return 'care.narvar.com';
    case 'staging':
      return 'pdx.narvar.qa';
    default:
      return null;
  }
};

const normalizeTraceFrames = (body: unknown): void => {
  const host = canonicalAssetHost();
  if (!host) {
    return;
  }
  if (!isRecord(body) || !('trace' in body)) {
    return;
  }
  const trace = body.trace;
  if (!isRecord(trace) || !Array.isArray(trace.frames)) {
    return;
  }
  for (const frame of trace.frames) {
    if (!isRecord(frame) || typeof frame.filename !== 'string') {
      continue;
    }
    try {
      const url = new URL(frame.filename);
      if (!url.pathname.endsWith('.js')) {
        continue;
      }
      if (!url.hostname.includes('narvar')) {
        continue;
      }
      url.hostname = host;
      frame.filename = url.href;
    } catch {
      // leave frame as-is
    }
  }
};

const buildLogDiagnostics = (): Record<string, unknown> => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    return {
      pathname: window.location.pathname,
      hrefOrigin: window.location.origin,
      navigatorOnLine:
        typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      ...(lastKnownRouteForErrors
        ? { lastRoute: lastKnownRouteForErrors }
        : {}),
    };
  } catch {
    return {};
  }
};

const rollbar = new Rollbar({
  accessToken: env.VITE_ROLLBAR_ACCESS_TOKEN,
  autoInstrument: {
    network: true,
    navigation: true,
    connectivity: true,
    log: false,
    dom: false,
    networkResponseHeaders: false,
    networkResponseBody: false,
    networkRequestBody: false,
  },
  captureIp: false,
  enabled: env.VITE_ROLLBAR_ENABLED === true,
  environment: env.VITE_ENV_NAME,
  payload: {
    client: {
      javascript: {
        code_version: env.VITE_GIT_SHA,
        source_map_enabled: true,
      },
    },
  },
  scrubFields: ['email', 'token', 'user_ip', 'zipCode'],
  scrubPaths: [
    'body.trace.extra.request.variables.input.orderTokens.1.value',
    'body.trace.extra.request.variables.input.orderTokens.2.value',
  ],
  scrubTelemetryInputs: true,
  transform: (payload: Rollbar.Payload) => {
    sanitizePayloadCustom(
      payload as { custom?: unknown; body?: unknown; data?: unknown },
    );
    const { body } = payload;
    normalizeTraceFrames(body);

    if (body.telemetry) {
      body.telemetry = body.telemetry.map(
        (telemetry: Rollbar.Payload['telemetry']) => {
          return {
            ...telemetry,
            body: {
              ...telemetry.body,
              message: redactString(telemetry.body.message),
              stack: redactString(telemetry.body.stack),
              url: redactUrl(telemetry.body.url),
            },
          };
        },
      );
    }

    if (body.trace?.exception) {
      body.trace.exception = {
        ...body.trace.exception,
        message: redactSensitiveInfo(body.trace.exception.message),
      };
    }

    if (body.trace?.extra?.response?.errors) {
      body.trace.extra = {
        ...body.trace.extra,
        response: {
          ...body.trace.extra.response,
          errors: Object.keys(body.trace.extra.response.errors).map(
            (key: string) => {
              const gqlError = body.trace.extra.response.errors[key];
              return {
                ...gqlError,
                message: redactSensitiveInfo(gqlError.message),
              };
            },
          ),
        },
      };
    }

    const networkOperation = (
      body.trace?.extra as { networkOperation?: string } | undefined
    )?.networkOperation;
    if (networkOperation) {
      (payload as Rollbar.Payload & { fingerprint?: string }).fingerprint =
        `network-failure:${networkOperation}`;
    }

    payload.body = body;
  },
});

const mergeRollbarCustom = (
  patch: Partial<Record<string, string | null | undefined>>,
): void => {
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      continue;
    }
    if (value === null || value === '') {
      delete rollbarCustomPayload[key];
    } else {
      rollbarCustomPayload[key] = value;
    }
  }
  rollbar.configure({
    payload: {
      custom: { ...rollbarCustomPayload },
    },
  });
};

/**
 * Syncs Rollbar `payload.custom` from the current URL (request id, retailer, etc.).
 * Call on startup and whenever URL-driven context is refreshed.
 */
export const syncRollbarContextFromUrl = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const params = getParams();
  const retailer = params?.retailer ?? getRetailerFromUrl();
  mergeRollbarCustom({
    retailerMoniker: retailer,
    shid: params?.shid ?? null,
    requestId: params?.request ?? null,
    orderNumber: params?.order ?? params?.orderNumber ?? null,
  });
};

export const clientLogger = {
  logError: (
    error: unknown,
    context = 'Client application error',
    info?: unknown,
  ) => {
    const err = toError(error, context);

    if (isAbortError(err)) {
      return { correlationId: undefined };
    }

    const networkError = isNetworkError(err);
    if (networkError && pageIsUnloading) {
      return { correlationId: undefined };
    }

    const diagnostics = buildLogDiagnostics();
    const infoPayload =
      info !== undefined && info !== null
        ? typeof info === 'object' && !Array.isArray(info)
          ? (info as Record<string, unknown>)
          : { errorEventInfo: info as string | number | boolean }
        : {};
    const networkOperation = networkError
      ? getNetworkOperation(err)
      : undefined;
    const reportPayload = {
      ...diagnostics,
      ...infoPayload,
      ...(networkOperation ? { networkOperation } : {}),
    };
    const { uuid } = networkError
      ? rollbar.warning(err, reportPayload)
      : rollbar.error(err, reportPayload);
    return { correlationId: uuid };
  },
  associateErrorsWithRetailer: (retailer: {
    retailerMoniker?: string | null;
    shid?: string | null;
  }) => {
    mergeRollbarCustom({
      retailerMoniker: retailer.retailerMoniker,
      shid: retailer.shid,
    });
  },
  recordRouteChange: (pathname: string) => {
    lastKnownRouteForErrors = pathname;
    mergeRollbarCustom({ lastRoute: pathname });
  },
};
